import { memo, useCallback, useState } from "react";
import { Check } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

// Rendu visuel d'un niveau selon le type d'échelle du système personnalisé.
export function renderLevel(level, scale, size = "md") {
  const box = size === "sm" ? "h-6 w-6" : "h-9 w-full";
  if (!level || level.is_none) {
    return <span className={`inline-flex ${size === "sm" ? "h-6 w-6" : "h-9 w-9"} items-center justify-center rounded-md border border-slate-300 bg-white text-slate-400 dark:border-white/15 dark:bg-transparent`}>—</span>;
  }
  if (scale === "color") {
    return <span className={`inline-block ${box} rounded-md ${level.color || "bg-slate-300"}`} />;
  }
  if (scale === "number") {
    return <span className={`inline-flex ${size === "sm" ? "h-6 min-w-6 px-1.5" : "h-9 w-full"} items-center justify-center rounded-md bg-slate-100 text-sm font-semibold text-slate-700 dark:bg-white/10 dark:text-slate-200`}>{level.value}</span>;
  }
  if (scale === "custom") {
    return (
      <span className={`inline-flex ${size === "sm" ? "" : "w-full justify-center"} items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${level.color ? `${level.color} text-white` : "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-slate-200"}`}>
        {level.symbol && <span>{level.symbol}</span>}
        <span className="truncate">{level.label}</span>
      </span>
    );
  }
  // texte
  return <span className={`inline-flex ${size === "sm" ? "" : "w-full justify-center"} items-center rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700 dark:bg-white/10 dark:text-slate-200`}>{level.label}</span>;
}

// Cellule d'évaluation pour un système personnalisé (menu déroulant, même clavier que ColorCell).
export const SystemCell = memo(function SystemCell({
  passportId, criterion, value, levels, scale, studentName, rowIndex, colIndex,
  onSelect, onNavigate, registerCell, size = "md",
}) {
  const [open, setOpen] = useState(false);
  const lg = size === "lg";
  const current = levels.find((l) => l.id === value) || levels.find((l) => l.is_none) || levels[0];

  const setRef = useCallback((el) => registerCell(rowIndex, colIndex, el), [registerCell, rowIndex, colIndex]);

  const handleKeyDown = useCallback((e) => {
    if (open) return;
    const map = { ArrowRight: "right", ArrowLeft: "left", ArrowUp: "up", ArrowDown: "down" };
    if (map[e.key]) { e.preventDefault(); onNavigate(map[e.key], rowIndex, colIndex); }
  }, [open, onNavigate, rowIndex, colIndex]);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        ref={setRef}
        onKeyDown={handleKeyDown}
        title={current?.label}
        aria-label={`${studentName}, ${criterion} : ${current?.label}. Entrée pour choisir le niveau.`}
        className={`flex w-full items-center justify-center rounded-md ${lg ? "min-h-[48px] p-1" : "p-0.5"} ring-offset-2 ring-offset-white transition-transform duration-100 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 data-[state=open]:ring-2 data-[state=open]:ring-blue-700 dark:ring-offset-[#0f0f0f]`}
        data-testid={`color-cell-${passportId}-${criterion}`}
        data-level={value}
      >
        {renderLevel(current, scale)}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={lg ? "w-[calc(100vw-3rem)] max-w-sm" : "w-64"} data-testid={`color-menu-${passportId}-${criterion}`}>
        {levels.map((l) => {
          const active = l.id === value;
          return (
            <DropdownMenuItem
              key={l.id}
              onSelect={() => onSelect(passportId, criterion, l.id)}
              className={`flex items-center gap-2.5 ${lg ? "min-h-[48px] py-2.5" : ""} ${active ? "bg-slate-100 dark:bg-white/10" : ""}`}
              data-testid={`color-option-${passportId}-${criterion}-${l.id}`}
              data-level={l.id}
            >
              <span className="shrink-0">{renderLevel(l, scale, "sm")}</span>
              <span className={`flex-1 ${lg ? "text-base" : "text-sm"} text-slate-700 dark:text-slate-200`}>{l.label}</span>
              {active && <Check size={15} className="shrink-0 text-slate-500" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

// Légende d'un système personnalisé (puces + libellés, dans l'ordre défini).
export function SystemLegend({ system, className = "" }) {
  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1.5 ${className}`} data-testid="color-legend">
      {system.levels.map((l) => (
        <div key={l.id} className="flex items-center gap-1.5" title={l.label} data-testid={`legend-item-${l.id}`}>
          {renderLevel(l, system.scale_type, "sm")}
          <span className="text-xs text-slate-500 dark:text-slate-400">{l.label}</span>
        </div>
      ))}
    </div>
  );
}
